<template>
  <div class="location-summary">
    <h3 class="global-h3 location-summary__title">
      <IconEmbedded name="map-pin_2-5" :size="20" style="margin-right: 4px; flex-shrink: 0" />
      <template v-if="isEditMode">
        <TextareaSubtle
          v-model:value="editableTitle"
          :isWithEditButton="false"
          :isBorderAlwaysVisible="true"
          :isDisabled="isSaving"
          :fontSize="22"
          :fontWeight="700"
          :lineHeightCoeff="1.18"
          :fakeBgSize="{
            width: 'calc(100% - 0px)',
            minWidth: 'calc(100% - 0px)',
            height: 'calc(100% - 8px)',
          }"
          class="location-summary__title-editor"
        />
      </template>
      <template v-else>
        <span>{{ location.title }}</span>
        <IconEmbedded
          class="location-summary__edit-icon"
          name="pencil_2-5"
          :size="18"
          color="rgba(0, 0, 0, 0.55)"
          @click="enterEditMode"
        />
      </template>
    </h3>

    <div v-if="isEditMode" class="location-summary__description location-summary__description--editing">
      <textarea class="global-textarea" v-model="editableSummary" :disabled="isSaving"></textarea>
    </div>
    <p v-else-if="location.summaryByCreator" class="location-summary__description">"{{ location.summaryByCreator?.trim() }}"</p>

    <div v-if="isEditMode" class="location-summary__buttons-panel">
      <SrpButton class="location-summary__button" size="small" color="orange" :isDisabled="isSaving" @click="saveEdits">
        <template #icon><IconEmbedded name="save_2-5" :size="18" /></template>
        Save
      </SrpButton>
      <SrpButton class="location-summary__button" size="small" fill="outlined" color="gray" :isDisabled="isSaving" @click="cancelEdits">
        Cancel
      </SrpButton>
      <NoteWithIcon class="location-summary__edit-warning" color="yellow" size="tiny">
        <template #text>Edits are intended for smaller corrections rather than sweeping changes.</template>
      </NoteWithIcon>
    </div>

    <HighlightsSection v-if="location.mediaIds?.length" class="location-summary__highlights" :highlightAssetIds="location.mediaIds" :visibleLines="200" :stretchToFill="false" />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { CollabAdventureLocationSummary } from "@contracts/collabLocations";
import CollabLocationsRepo from "@repos/CollabLocationsRepo";

// Components
import HighlightsSection from "@components/HighlightsSection.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import TextareaSubtle from "@components/ui/TextareaSubtle.vue";

const props = defineProps<{
  location: CollabAdventureLocationSummary;
  communityId: string;
  collabInputId: string;
}>();

const isEditMode = ref(false);
const isSaving = ref(false);
const editableTitle = ref("");
const editableSummary = ref("");
const titleCache = ref("");
const summaryCache = ref("");

function enterEditMode() {
  titleCache.value = props.location.title ?? "";
  summaryCache.value = props.location.summaryByCreator ?? "";
  editableTitle.value = titleCache.value;
  editableSummary.value = summaryCache.value;
  isEditMode.value = true;
}

function cancelEdits() {
  editableTitle.value = titleCache.value;
  editableSummary.value = summaryCache.value;
  isEditMode.value = false;
}

async function saveEdits() {
  if (!props.location.id) return;

  const trimmedTitle = editableTitle.value?.trim() ?? "";
  if (!trimmedTitle) return;

  const trimmedSummary = editableSummary.value?.trim() || null;

  isSaving.value = true;
  const updated = await CollabLocationsRepo.updateLocationTitleAndSummary(
    props.communityId,
    props.collabInputId,
    props.location.id,
    trimmedTitle,
    trimmedSummary
  );
  props.location.title = updated.title;
  props.location.summaryByCreator = updated.summaryByCreator;
  isSaving.value = false;
  isEditMode.value = false;
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

.location-summary {
  &__title {
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__title-editor {
    flex: 1;
    min-width: 0;
  }

  &__edit-icon {
    margin-left: 4px;
    flex-shrink: 0;
    cursor: pointer;
  }

  &__description {
    white-space: pre-wrap;
    margin-bottom: 12px;
    margin-right: 70px;
    color: rgba(0, 0, 0, 0.7);

    &--editing {
      color: rgba(0, 0, 0, 0.7);
      margin-right: 0;

      .global-textarea {
        width: 100%;
        min-height: 100px;
        color: rgba(0, 0, 0, 0.7);
        font: 16px/22px "Quicksand", sans-serif;
      }
    }
  }

  &__buttons-panel {
    gap: 10px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  &__edit-warning {
    flex: 0 0 auto;
    width: fit-content;
    max-width: 100%;
  }

  @media (max-width: $mobile-max-width) {
    &__description {
      margin-right: 20px;
    }
  }
  @media (min-width: $mobile-max-width) and (max-width: $tablet-max-width) {
    &__description {
      margin-right: 40px;
    }
  }
}
</style>
